//TODO : ADD USAGE
//USAGE EXAMPLE
//FOR REFERENCE CHECK AddEditDocument.tsx File
//FOR REFERENCE CHECK ImportCatalogItem.tsx File

import { FileUploadErrorIcon } from '@assets';
import {
  Box,
  CircularProgress,
  ConfirmationDialogBox,
  FormLabel,
  Grid,
  Stack,
} from '@atgfr8/atg-ui-tailwind';
import { DeleteIcon, InformationIcon } from '@atgfr8/icons';
import { CategoryT } from '@common/types';
import { LinkComponent } from '@components/Link';
import { v4ServeDocumentUrl } from '@constants/urls';
import {
  Component,
  createSignal,
  For,
  mergeProps,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';

import { HoverPop } from '../HoverPop/HoverPop';
import { FileCategory } from './FileCategory';
import { classes } from './FileUpload.style';
import { DocumentModel } from './types';

export type CommonFileUploadFiles = {
  name: string;
  dateAdded?: string;
  id?: number;
  url?: string;
  size?: number;
  taggedCategoryKeys?: string[];
};

export type FileTypes =
  | 'pdf'
  | 'jpeg'
  | 'jpg'
  | 'png'
  | 'gif'
  | 'doc'
  | 'docx'
  | 'csv'
  | 'xls'
  | 'xlsx'
  | 'ppt'
  | 'pptx'
  | 'txt'
  | 'tif'
  | 'xks'
  | 'html'
  | 'msg'
  | 'odt';
// Add more file types as needed

type Props = {
  id?: string;
  removeFile?: (
    index: number,
    item: CommonFileUploadFiles,
  ) => Promise<void> | void;
  maxFileSize?: number;
  acceptedFileTypes?: FileTypes[];
  isRemoveFile?: boolean;
  shouldUploadOnSelect?: boolean;
  uploadedFiles?: CommonFileUploadFiles[];
  apiCallBackFn?: (file: File) => Promise<void>;
  error?: string | string[] | null;
  onSelectedFile?: (file: File) => void;
  onSelectedFileRemove?: () => void;
  customValidation?: (file: File) => boolean;
  isMultipleUpload?: boolean;
  height?: number;
  width?: number;
  noDomainUrl?: boolean;
  label?: string;
  disabled?: boolean;
  alreadyUploadedDocuments?: DocumentModel[];
  required?: boolean;
  showAttachFiles?: boolean;
  fileRestrictionMessage?: string;
  categories?: CategoryT[];
  isAdmin?: boolean;
  handleFullUploadCustom?: (files: FileList | undefined) => void;
};

const DEFAULT_LABEL = 'Click or Drag a File To Upload';
const DEFAULT_HEIGHT = 150;
const DEFAULT_WIDTH = 100;
const DEFAULT_MAX_SIZE = 5242880;

const CommonFileUpload: Component<Props> = (props) => {
  props = mergeProps({ maxFileSize: 5242880 }, props);

  const [loading, setLoading] = createSignal(false);
  const [invalidFile, setInvalidFile] = createSignal<
    'fileType' | 'fileSize' | 'none'
  >('none');
  const [fileToDelete, setFileToDelete] = createSignal<{
    index: number;
    file: CommonFileUploadFiles;
  } | null>(null);
  const [inputRef, setInputRef] = createSignal<HTMLInputElement>();
  const [dropZoneRef, setDropZoneRef] = createSignal<HTMLDivElement>();
  const [buttonRef, setButtonRef] = createSignal<HTMLDivElement>();
  const [getIsDialogOpen, setIsDialogOpen] = createSignal(false);
  const [invalidFileTypeErrors, setInvalidFileTypeErrors] = createSignal<{
    customErrors: string[];
    fileTypeErrors: string[];
    fileSizeErrors: string[];
  }>({
    customErrors: [],
    fileTypeErrors: [],
    fileSizeErrors: [],
  });

  const handleFileValidation = (file: File | null) => {
    if (!file) {
      return {
        isCustom: false,
        isFileType: false,
        isFileSize: false,
      };
    }

    const isCustom = props.customValidation
      ? props.customValidation(file)
      : true;
    const isFileType = checkFileType(file);
    const isFileSize = file.size <= (props.maxFileSize ?? DEFAULT_MAX_SIZE);

    return {
      isCustom,
      isFileType,
      isFileSize,
    };
  };

  const handleFileChangeOrDrop = async (
    file: File | null,
    e: DragEvent | Event,
  ) => {
    const { isCustom, isFileType, isFileSize } = handleFileValidation(file);
    if (!isCustom || !Boolean(isFileType) || !isFileSize) {
      setInvalidFileTypeErrors((prev) => ({
        ...prev,
        customErrors: !isCustom
          ? [...prev.customErrors, file?.name as string]
          : prev.customErrors,
        fileTypeErrors: !Boolean(isFileType)
          ? [...prev.fileTypeErrors, file?.name as string]
          : prev.fileTypeErrors,
        fileSizeErrors: !isFileSize
          ? [...prev.fileSizeErrors, file?.name as string]
          : prev.fileSizeErrors,
      }));

      if (!isFileSize) setInvalidFile('fileSize');
      if (!Boolean(isFileType)) setInvalidFile('fileType');
      return;
    }

    const shouldUpload =
      Boolean(props.shouldUploadOnSelect) || Boolean(props.isMultipleUpload);

    if (!shouldUpload) {
      props.onSelectedFile?.(file!);
    } else {
      props.apiCallBackFn && (await props.apiCallBackFn(file!));
    }
    // TO clear the input value after file upload or drop
    if (e.target) {
      (e.target as HTMLInputElement).value = '';
    }
  };

  const handleChange = async (e: Event) => {
    try {
      setLoading(true);
      setInvalidFile('none');
      setInvalidFileTypeErrors({
        customErrors: [],
        fileTypeErrors: [],
        fileSizeErrors: [],
      });
      const target = e.target as HTMLInputElement;
      const files = target.files ?? undefined;

      if (props.handleFullUploadCustom) {
        props.handleFullUploadCustom(files);
      } else if (props.isMultipleUpload === true) {
        const filePromises: Promise<void>[] = [];
        for (let i = 0; i < (files?.length ?? 0); i++) {
          filePromises.push(handleFileChangeOrDrop(files?.[i] ?? null, e));
        }
        await Promise.all(filePromises);
      } else {
        const file = files?.[0];
        await handleFileChangeOrDrop(file!, e);
      }
    } finally {
      setLoading(false);
      (e.target as HTMLInputElement).value = '';
    }
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    if (props.disabled) {
      return;
    }
    setInvalidFile('none');
    setLoading(true);
    const dropZone = dropZoneRef();
    if (dropZone) {
      dropZone.style.backgroundColor = 'rgb(242, 246, 248)';
      dropZone.style.border = '3px solid transparent';
    }
    if (Boolean(props.isMultipleUpload)) {
      const filePromises = [];
      if (props.handleFullUploadCustom) {
        props.handleFullUploadCustom(e.dataTransfer?.files ?? undefined);
      } else {
        for (let i = 0; i < (e.dataTransfer?.files.length ?? 0); i++) {
          filePromises.push(
            handleFileChangeOrDrop(e.dataTransfer?.files[i] ?? null, e),
          );
        }
        await Promise.all(filePromises);
      }
    } else {
      const file = e.dataTransfer?.files[0];
      await handleFileChangeOrDrop(file!, e);
    }
    setLoading(false);
  };

  const checkFileType = (file: File) => {
    const fileExtension = file.name.split('.').pop();
    if (
      !Boolean(props.acceptedFileTypes) ||
      props.acceptedFileTypes?.length === 0
    )
      return true;
    return props.acceptedFileTypes?.includes(fileExtension as FileTypes);
  };

  const handleDelete = async () => {
    if (fileToDelete()) {
      const index = fileToDelete()?.index;
      const file = fileToDelete()?.file;
      if (index !== undefined && file !== undefined) {
        if (props.removeFile) {
          await props.removeFile(Number(index), file);
        }
      }
      setFileToDelete(null);
    }
  };

  const removeUploadedFile = (index: number, file: CommonFileUploadFiles) => {
    setFileToDelete({
      index: index,
      file,
    });
    // openDialogBox('customFileUploadDeleteDialog');
    setIsDialogOpen(true);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    const dropZone = dropZoneRef();

    if (dropZone) {
      dropZone.style.backgroundColor = '#d3ebf0';
      dropZone.style.border = '3px solid #026ea1';
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    const dropZone = dropZoneRef();

    if (dropZone) {
      dropZone.style.backgroundColor = 'rgb(242, 246, 248)';
      dropZone.style.border = '3px solid transparent';
    }
  };

  onMount(() => {
    const dropZone = dropZoneRef();

    if (dropZone) {
      dropZone.addEventListener('dragover', handleDragOver);
      dropZone.addEventListener('dragenter', handleDragOver);
      dropZone.addEventListener('dragleave', handleDragLeave);
      dropZone.addEventListener('drop', handleDrop);
      dropZone.style.transition = '0.2s ease-in-out';
      dropZone.style.border = '3px solid transparent';
      dropZone.style.borderRadius = '6px';
    }
    buttonRef()?.addEventListener('click', () => {
      inputRef()?.click();
    });
  });

  onCleanup(() => {
    dropZoneRef()?.removeEventListener('dragover', handleDragOver);
    dropZoneRef()?.removeEventListener('dragenter', handleDragOver);
    dropZoneRef()?.removeEventListener('dragleave', handleDragLeave);
    dropZoneRef()?.removeEventListener('drop', handleDrop);
  });

  const hasCategories = () => Boolean(props.categories?.length);
  const hasUploadedFiles = () => Boolean(props.uploadedFiles?.length);

  return (
    <>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        ref={setDropZoneRef}
        class="relative w-full h-full"
      >
        <Grid
          container
          style={classes.iconLabelContainer(props.height ?? DEFAULT_HEIGHT)}
          ref={setButtonRef}
        >
          <FormLabel
            style={{
              color: (props.disabled ?? false) || loading() ? 'lightgrey' : '',
              'margin-top': '6px',
            }}
            class="min-w-0 max-w-full flex-1"
          >
            {loading() ? (
              <>Files are uploading please wait....</>
            ) : (
              (props.label ?? (
                <div class="flex min-w-0 max-w-full items-center justify-center overflow-hidden px-2">
                  <div class="min-w-0 truncate">{DEFAULT_LABEL}</div>
                  <div class="relative z-10 ml-2 shrink-0">
                    <HoverPop anchor={<InformationIcon />} render={true}>
                      <div class="p-2">
                        {Boolean(props.fileRestrictionMessage)
                          ? props.fileRestrictionMessage
                          : `SVG, PNG, JPG, GIF, CSV, TIFF, BMP, or, PDF (max. ${
                              (props.maxFileSize ?? DEFAULT_MAX_SIZE) / 1048576
                            }mb)`}
                      </div>
                    </HoverPop>
                  </div>
                </div>
              ))
            )}
          </FormLabel>
        </Grid>
        <div
          style={{
            border: '1px dashed lightgrey',
            'border-radius': '5px',
            width: '100%',
          }}
        >
          <input
            {...props}
            type="file"
            multiple
            accept={
              props.acceptedFileTypes?.map((type) => `.${type}`).join(',') ??
              '*/*'
            }
            ref={setInputRef}
            style={{
              height: `${props.height ?? DEFAULT_HEIGHT}px`,
              width: `${props.width ?? DEFAULT_WIDTH}%`,
            }}
            class="opacity-0 cursor-pointer z-2"
            disabled={(props.disabled ?? false) || loading()}
            onChange={handleChange}
            required={props.required}
          />
        </div>
        {invalidFile() !== 'none' && (
          <Box
            backgroundColor="#FFD8DF"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={1}
            class="gap-2"
          >
            <span class="text-[#952828] font-bold">Status:</span>
            <span class="flex-1">
              <span class="font-medium">
                {invalidFile() === 'fileType'
                  ? 'Invalid File Type'
                  : 'File Size Exceeded'}{' '}
              </span>
              <Show when={Boolean(props.isMultipleUpload)}>
                <ul class="list-disc ml-5">
                  <For
                    each={
                      invalidFile() === 'fileType'
                        ? invalidFileTypeErrors().fileTypeErrors
                        : invalidFileTypeErrors().fileSizeErrors
                    }
                  >
                    {(error) => {
                      return (
                        <li class="break-all">
                          <span>{error}</span>
                        </li>
                      );
                    }}
                  </For>
                </ul>
              </Show>
            </span>
            <span>
              <img src={FileUploadErrorIcon} alt="File Upload Error" />
            </span>
          </Box>
        )}
        <Show when={hasCategories() || hasUploadedFiles()}>
          <Box class="max-h-[400px] overflow-y-auto py-2">
            <Show when={!hasCategories()}>
              <For each={props.uploadedFiles ?? []}>
                {(file, index) => {
                  return (
                    <Stack
                      direction="row"
                      alignItems="center"
                      class="p-1 gap-2"
                    >
                      <Box display="flex" class="gap-2" alignItems="center">
                        {Boolean(file.url) ? (
                          <Box class="text-left break-all">
                            <LinkComponent
                              url={
                                Boolean(props.noDomainUrl)
                                  ? (file.url ?? '')
                                  : `${v4ServeDocumentUrl.doc(
                                      encodeURIComponent(file.url as string),
                                    )}`
                              }
                              target={'_blank'}
                              title={file.name}
                              download={file.name}
                            />
                          </Box>
                        ) : (
                          <span>{file.name}</span>
                        )}
                      </Box>
                      <Show
                        when={
                          Boolean(props.isRemoveFile) && Boolean(props.isAdmin)
                        }
                      >
                        <DeleteIcon
                          class="text-[#B00020] cursor-pointer"
                          onclick={() => removeUploadedFile(index(), file)}
                        />
                      </Show>
                    </Stack>
                  );
                }}
              </For>
            </Show>
            <Show when={hasCategories()}>
              <For each={props.categories ?? []}>
                {(category) => {
                  return (
                    <Box>
                      <FileCategory
                        category={category}
                        files={props.uploadedFiles ?? []}
                        onUpload={() => {
                          inputRef()?.click();
                        }}
                        onDelete={(file) => {
                          const fileIdx = props.uploadedFiles?.findIndex(
                            (f) => f.name === file.name,
                          );

                          if (fileIdx !== undefined && fileIdx >= 0) {
                            removeUploadedFile(fileIdx, file);
                          }
                        }}
                      />
                    </Box>
                  );
                }}
              </For>
            </Show>
          </Box>
        </Show>
        <Show when={loading()}>
          <Box style={classes.BoxStyles()}>
            <Box>
              <CircularProgress size={50} />
            </Box>
          </Box>
        </Show>
      </div>
      <ConfirmationDialogBox
        dataTestId="customFileUploadDeleteDialog"
        id="customFileUploadDeleteDialog"
        title="Are you sure you want to delete this document?"
        onSubmit={handleDelete}
        onSubmitText="Delete"
        onCancel={() => setFileToDelete(null)}
        isDialogOpen={getIsDialogOpen()}
        closeDialogBox={(_reason) => setIsDialogOpen(false)}
      />
    </>
  );
};

export default CommonFileUpload;
